/** Native clip-local volume automation. Canonical implementation; synced to the frontend. */
export interface VolumePoint {
  /** Clip-local timeline ticks. */
  time: number
  /** Relative level in dB; null is exact silence. */
  gainDb: number | null
  /** Interpolation from this point to the next. Linear interpolates amplitude. */
  curve?: 'linear' | 'hold'
}

export const MAX_VOLUME_POINTS = 256
export const MIN_VOLUME_DB = -120
export const MAX_VOLUME_DB = 12

export function volumeDbToGain(value: number | null): number {
  return value === null ? 0 : 10 ** (value / 20)
}

export function volumeGainToDb(value: number): number | null {
  return value <= 0 ? null : Math.max(MIN_VOLUME_DB, 20 * Math.log10(value))
}

/** How a validation message names a time; MCP renders the caller's own units, the default is ticks. */
export interface VolumePointsErrorFormat {
  /** Render the time of the point at `index`. */
  time: (ticks: number, index: number) => string
  /** Render the clip's usable range, from 0 to `duration`. */
  range: (duration: number) => string
}

const tickFormat: VolumePointsErrorFormat = {
  time: ticks => `${ticks} ticks`,
  range: duration => `0 and ${duration} ticks`,
}

/**
 * Validate without sorting: conflicting or ambiguous points must not silently change an edit.
 * Each cause gets its own message naming the point so a caller can fix the right thing;
 * on MCP the message is the whole feedback channel.
 */
export function volumePointsError(
  points: readonly VolumePoint[] | undefined,
  duration: number,
  format: VolumePointsErrorFormat = tickFormat,
): string | null {
  if (points === undefined) return null
  if (!Array.isArray(points) || points.length > MAX_VOLUME_POINTS)
    return `Use at most ${MAX_VOLUME_POINTS} volume points.`
  let previous = -1
  for (const [index, point] of points.entries()) {
    if (!point) return `Volume point at index ${index} is missing.`
    if (!Number.isSafeInteger(point.time))
      return `Volume point at index ${index} has an invalid time (${String(point.time)}); times must be whole ticks.`
    if (point.time < 0)
      return `Volume point at index ${index} (time ${format.time(point.time, index)}) is before the start of the shot; times must be between ${format.range(duration)}.`
    if (point.time > duration)
      return `Volume point at index ${index} (time ${format.time(point.time, index)}) is past the end of the shot; times must be between ${format.range(duration)}.`
    if (index > 0 && point.time <= previous)
      return `Volume point at index ${index} (time ${format.time(point.time, index)}) is not after point at index ${index - 1} (time ${format.time(previous, index - 1)}); times must be strictly increasing.`
    if (
      point.gainDb !== null &&
      (!Number.isFinite(point.gainDb) || point.gainDb < MIN_VOLUME_DB || point.gainDb > MAX_VOLUME_DB)
    ) {
      return `Volume point at index ${index} has level ${String(point.gainDb)}; levels must be between ${MIN_VOLUME_DB} and ${MAX_VOLUME_DB} dB, or null for silence.`
    }
    if (point.curve !== undefined && point.curve !== 'linear' && point.curve !== 'hold')
      return `Volume point at index ${index} has curve "${String(point.curve)}"; choose linear or hold.`
    previous = point.time
  }
  return null
}

/** Index of the point at or immediately before time; -1 means before the first point. */
function pointBefore(points: readonly VolumePoint[], time: number): number {
  let low = 0
  let high = points.length
  while (low < high) {
    const mid = (low + high) >>> 1
    if (points[mid].time <= time) low = mid + 1
    else high = mid
  }
  return low - 1
}

/** Endpoint values extend to the clip edges. An absent/empty curve is unity. */
export function volumeGainAt(points: readonly VolumePoint[] | undefined, time: number): number {
  if (!points?.length) return 1
  const index = pointBefore(points, time)
  if (index < 0) return volumeDbToGain(points[0].gainDb)
  const left = points[index]
  const right = points[index + 1]
  const gain = volumeDbToGain(left.gainDb)
  if (!right || left.curve === 'hold') return gain
  return gain + (volumeDbToGain(right.gainDb) - gain) * ((time - left.time) / (right.time - left.time))
}

/** Preserve the audible curve when trimming, splitting, or flattening a nested sequence. */
export function windowVolumePoints(
  points: readonly VolumePoint[] | undefined,
  offset: number,
  duration: number,
): VolumePoint[] | undefined {
  if (!points?.length || duration <= 0) return undefined
  offset = Math.round(offset)
  duration = Math.round(duration)
  const end = offset + duration
  const result: VolumePoint[] = []
  if (offset >= points[0].time) {
    const index = pointBefore(points, offset)
    const curve = points[index].curve
    result.push({ time: 0, gainDb: volumeGainToDb(volumeGainAt(points, offset)), ...(curve ? { curve } : {}) })
  }
  for (const point of points) {
    if (point.time > offset && point.time < end) result.push({ ...point, time: point.time - offset })
  }
  if (end <= points[points.length - 1].time) {
    result.push({ time: duration, gainDb: volumeGainToDb(volumeGainAt(points, end)) })
  }
  if (!result.length) result.push({ time: 0, gainDb: volumeGainToDb(volumeGainAt(points, offset)) })
  return result
}

/** A retime changes clip-local time while preserving each point's relative placement. */
export function retimeVolumePoints(
  points: readonly VolumePoint[] | undefined,
  oldDuration: number,
  newDuration: number,
): VolumePoint[] | undefined {
  if (!points?.length || oldDuration <= 0 || newDuration <= 0) return undefined
  const byTime = new Map<number, VolumePoint>()
  for (const point of points) {
    const time = Math.min(newDuration, Math.round((point.time * newDuration) / oldDuration))
    byTime.set(time, { ...point, time })
  }
  return [...byTime.values()]
}

/** Preserve clip-local automation through a regular source trim; movement alone changes nothing. */
export function trimmedVolumePoints(
  clip: { trimIn: number; duration: number; volumePoints?: VolumePoint[] },
  next: { trimIn: number; duration: number },
  speed = 1,
): VolumePoint[] | undefined {
  if (clip.trimIn === next.trimIn && clip.duration === next.duration) return clip.volumePoints
  return windowVolumePoints(clip.volumePoints, (next.trimIn - clip.trimIn) / speed, next.duration)
}

/** Canonical source/timeline mapping; shared by the editor, MCP, and export. */

export const MIN_CLIP_SPEED = 0.25
export const MAX_CLIP_SPEED = 4
export interface ClipTiming {
  trimIn: number
  trimOut: number
  duration: number
  speed?: number
  loop?: boolean
}
export function clipSpeed(clip: { speed?: number }): number {
  return clip.speed ?? 1
}
export function sourceWindowTicks(clip: Pick<ClipTiming, 'trimIn' | 'trimOut'>): number {
  return clip.trimOut - clip.trimIn
}
export function clipTimelineDuration(clip: ClipTiming): number {
  return clip.loop ? clip.duration : sourceWindowTicks(clip) / clipSpeed(clip)
}
export function sourceTimeAt(
  clip: Pick<ClipTiming, 'trimIn' | 'trimOut' | 'speed' | 'loop'>,
  localTime: number,
): number {
  const window = sourceWindowTicks(clip)
  if (clip.loop && window <= 0) return clip.trimIn
  const elapsed = localTime * clipSpeed(clip)
  return clip.trimIn + (clip.loop ? elapsed % window : elapsed)
}
export function clipTimingError(clip: ClipTiming): string | null {
  if (clip.loop !== undefined && typeof clip.loop !== 'boolean') return 'Choose a valid source range and duration.'
  if (!clip.loop && clip.speed === undefined) return null // Existing projects retain their historical timing.
  if (
    clip.speed !== undefined &&
    (!Number.isFinite(clip.speed) || clip.speed < MIN_CLIP_SPEED || clip.speed > MAX_CLIP_SPEED)
  )
    return 'Choose a speed between 0.25× and 4×.'
  if (
    !Number.isFinite(clip.duration) ||
    clip.duration <= 0 ||
    !Number.isFinite(clip.trimIn) ||
    clip.trimIn < 0 ||
    !Number.isFinite(clip.trimOut) ||
    clip.trimOut <= clip.trimIn
  )
    return 'Choose a valid source range and duration.'
  if (!clip.loop && Math.abs(clip.trimOut - clip.trimIn - clipSpeed(clip) * clip.duration) > 1.01)
    return 'The source range, speed, and duration do not agree. Set speed or fit to duration again.'
  return null
}
/** Preserve source bounds. Store the exact rate after snapping duration to a timeline frame. */
export function retimeClip<T extends ClipTiming & { volumePoints?: VolumePoint[]; fadeIn?: number; fadeOut?: number }>(
  clip: T,
  edit: { speed?: number; fitDuration?: number },
  frameTicks = 2000,
): Pick<T, 'duration'> & { speed: number; volumePoints?: VolumePoint[]; fadeIn?: number; fadeOut?: number } {
  if ((edit.speed === undefined) === (edit.fitDuration === undefined))
    throw new Error('Choose speed or fit to duration, not both.')
  const sourceDuration = clip.trimOut - clip.trimIn
  const requested = edit.fitDuration ?? (clip.loop ? clip.duration : sourceDuration / edit.speed!)
  if (!Number.isFinite(requested) || requested <= 0) throw new Error('Enter a positive duration or speed.')
  const duration =
    clip.loop && edit.speed !== undefined
      ? clip.duration
      : Math.max(frameTicks, Math.round(requested / frameTicks) * frameTicks)
  const speed = clip.loop ? (edit.speed ?? clipSpeed(clip)) : sourceDuration / duration
  const error = clipTimingError({ ...clip, duration, speed })
  if (error) throw new Error(error)
  return {
    duration,
    speed,
    volumePoints: retimeVolumePoints(clip.volumePoints, clip.duration, duration),
    fadeIn: clip.fadeIn ? Math.min(duration, Math.round((clip.fadeIn * duration) / clip.duration)) : undefined,
    fadeOut: clip.fadeOut ? Math.min(duration, Math.round((clip.fadeOut * duration) / clip.duration)) : undefined,
  }
}
